import React, { useState } from 'react';
import { DocumentSection } from '../../../core/ast/document.types';
import { useDocument } from '../../../core/state/DocumentContext';
import { TextBlockEditor } from './TextBlockEditor';
import { FormulaBlockEditor } from './FormulaBlockEditor';
import { TableBlockEditor } from './TableBlockEditor';
import { ImageBlockEditor } from './ImageBlockEditor';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Trash2,
  Copy,
  ArrowUp,
  ArrowDown,
  Edit3,
  Sigma,
  Table as TableIcon,
  ImageIcon,
  FileMinus,
  Settings2,
  Scan,
} from 'lucide-react';

interface SectionItemEditorProps {
  section: DocumentSection;
  index: number;
  totalSections: number;
  onOpenOCR?: (sectionId: string, imagePreviewData?: string) => void;
}

export const SectionItemEditor: React.FC<SectionItemEditorProps> = ({
  section,
  index,
  totalSections,
  onOpenOCR,
}) => {
  const {
    updateSection,
    removeSection,
    moveSection,
    duplicateSection,
    toggleSectionEnabled,
    addBlock,
    removeBlock,
    updateBlockData,
    moveBlock,
    duplicateBlock,
  } = useDocument();

  const [showSectionSettings, setShowSectionSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleToggleCollapse = () => {
    updateSection(section.id, { collapsed: !section.collapsed });
  };

  return (
    <div
      id={`section-${section.id}`}
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--bg-surface)',
        overflow: 'hidden',
        opacity: section.enabled ? 1 : 0.6,
        transition: 'all var(--transition-fast)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* Section Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-2) var(--space-4)',
          backgroundColor: 'var(--bg-surface-subtle)',
          borderBottom: !section.collapsed ? '1px solid var(--border-subtle)' : 'none',
          userSelect: 'none',
        }}
      >
        <div
          onClick={handleToggleCollapse}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', flex: 1, minWidth: 0 }}
        >
          {section.collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--text-tertiary)', width: 22 }}>
            #{index + 1}
          </span>
          <input
            type="text"
            className="form-input"
            value={section.title}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => updateSection(section.id, { title: e.target.value })}
            style={{
              padding: '2px 6px',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              width: 'auto',
              minWidth: '160px',
              maxWidth: '320px',
              backgroundColor: 'transparent',
              borderColor: 'transparent',
            }}
          />
          {section.settings?.pageBreakBefore && (
            <span className="badge badge-neutral" style={{ fontSize: '9px', padding: '1px 4px' }}>
              Page Break
            </span>
          )}
        </div>

        {/* Section Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            type="button"
            className={`icon-btn ${showSectionSettings ? 'active' : ''}`}
            style={{ width: 26, height: 26 }}
            onClick={() => setShowSectionSettings(!showSectionSettings)}
            title="Section Page Break & Settings"
          >
            <Settings2 size={13} />
          </button>
          <button
            type="button"
            className="icon-btn"
            style={{ width: 26, height: 26 }}
            disabled={index === 0}
            onClick={() => moveSection(section.id, 'up')}
            title="Move Section Up"
          >
            <ArrowUp size={13} />
          </button>
          <button
            type="button"
            className="icon-btn"
            style={{ width: 26, height: 26 }}
            disabled={index === totalSections - 1}
            onClick={() => moveSection(section.id, 'down')}
            title="Move Section Down"
          >
            <ArrowDown size={13} />
          </button>
          <button
            type="button"
            className="icon-btn"
            style={{ width: 26, height: 26 }}
            onClick={() => duplicateSection(section.id)}
            title="Duplicate Section"
          >
            <Copy size={13} />
          </button>
          <button
            type="button"
            className="icon-btn"
            style={{ width: 26, height: 26 }}
            onClick={() => toggleSectionEnabled(section.id)}
            title={section.enabled ? 'Disable Section (Hides in Document)' : 'Enable Section'}
          >
            {section.enabled ? (
              <Eye size={13} color="var(--color-success)" />
            ) : (
              <EyeOff size={13} color="var(--text-tertiary)" />
            )}
          </button>

          {showDeleteConfirm ? (
            <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-danger btn-sm text-xs"
                style={{ fontSize: '10px', padding: '2px 6px' }}
                onClick={() => {
                  removeSection(section.id);
                  setShowDeleteConfirm(false);
                }}
              >
                Delete?
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm text-xs"
                style={{ fontSize: '10px', padding: '2px 4px' }}
                onClick={() => setShowDeleteConfirm(false)}
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="icon-btn"
              style={{ width: 26, height: 26 }}
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete Section"
            >
              <Trash2 size={13} color="var(--color-danger)" />
            </button>
          )}
        </div>
      </div>

      {/* Optional Section Settings Dropdown */}
      {showSectionSettings && (
        <div
          style={{
            padding: 'var(--space-2) var(--space-4)',
            backgroundColor: 'var(--bg-surface-subtle)',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '11px',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={section.settings?.pageBreakBefore || false}
              onChange={(e) =>
                updateSection(section.id, {
                  settings: {
                    ...section.settings,
                    pageBreakBefore: e.target.checked,
                  },
                })
              }
            />
            <span>Force page break before this section starts</span>
          </label>
          <button
            type="button"
            className="btn btn-ghost btn-sm text-xs"
            style={{ fontSize: '10px', padding: '1px 6px' }}
            onClick={() => setShowSectionSettings(false)}
          >
            Done
          </button>
        </div>
      )}

      {/* Expanded Section Body with Blocks */}
      {!section.collapsed && (
        <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {/* Blocks List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {section.blocks.map((block, bIdx) => (
              <div
                key={block.id}
                style={{
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--bg-surface)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                {/* Block Header Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '4px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', letterSpacing: '0.04em' }}>
                    {block.type.replace('_', ' ')} Block #{bIdx + 1}
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ width: 22, height: 22 }}
                      disabled={bIdx === 0}
                      onClick={() => moveBlock(section.id, block.id, 'up')}
                      title="Move Block Up"
                    >
                      <ArrowUp size={11} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ width: 22, height: 22 }}
                      disabled={bIdx === section.blocks.length - 1}
                      onClick={() => moveBlock(section.id, block.id, 'down')}
                      title="Move Block Down"
                    >
                      <ArrowDown size={11} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ width: 22, height: 22 }}
                      onClick={() => duplicateBlock(section.id, block.id)}
                      title="Duplicate Block"
                    >
                      <Copy size={11} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ width: 22, height: 22 }}
                      onClick={() => removeBlock(section.id, block.id)}
                      title="Delete Block"
                    >
                      <Trash2 size={11} color="var(--color-danger)" />
                    </button>
                  </div>
                </div>

                {/* Specific Block Editor Renderer */}
                {block.type === 'text' && (
                  <TextBlockEditor
                    block={block}
                    onUpdate={(dataUpdates) => updateBlockData(section.id, block.id, dataUpdates)}
                  />
                )}
                {block.type === 'formula' && (
                  <FormulaBlockEditor
                    block={block}
                    onUpdate={(dataUpdates) => updateBlockData(section.id, block.id, dataUpdates)}
                  />
                )}
                {block.type === 'table' && (
                  <TableBlockEditor
                    block={block}
                    onUpdate={(dataUpdates) => updateBlockData(section.id, block.id, dataUpdates)}
                  />
                )}
                {block.type === 'image' && (
                  <ImageBlockEditor
                    block={block}
                    onUpdate={(dataUpdates) => updateBlockData(section.id, block.id, dataUpdates)}
                    onTriggerOCR={
                      onOpenOCR ? (previewData) => onOpenOCR(section.id, previewData) : undefined
                    }
                  />
                )}
                {block.type === 'page_break' && (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FileMinus size={14} color="var(--primary-600)" />
                    Forced page break. Content below will render on the next paper sheet.
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add Block Palette */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2)',
              backgroundColor: 'var(--bg-surface-subtle)',
              borderRadius: 'var(--radius-sm)',
              border: '1px dashed var(--border-default)',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginRight: 4 }}>
              + Insert Content Block:
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm text-xs"
              onClick={() => addBlock(section.id, 'text')}
            >
              <Edit3 size={12} /> Text
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm text-xs"
              onClick={() => addBlock(section.id, 'formula')}
            >
              <Sigma size={12} /> KaTeX Math
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm text-xs"
              onClick={() => addBlock(section.id, 'table')}
            >
              <TableIcon size={12} /> Data Table
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm text-xs"
              onClick={() => addBlock(section.id, 'image')}
            >
              <ImageIcon size={12} /> Image / Diagram
            </button>
            {onOpenOCR && (
              <button
                type="button"
                className="btn btn-ghost btn-sm text-xs"
                style={{ color: 'var(--primary-600)', fontWeight: 600 }}
                onClick={() => onOpenOCR(section.id)}
                title="Scan document/image with Smart OCR"
              >
                <Scan size={12} /> OCR / Scan
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm text-xs"
              onClick={() => addBlock(section.id, 'page_break')}
            >
              <FileMinus size={12} /> Page Break
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
